// src/modules/pedidos/components/KitVinculo.js
//
// Muestra, para un renglón que es KIT, la posibilidad de vincular el "pedido de
// componentes" (los que los vendedores marcan con "COMPONENTES KIT" en la
// descripción) y, una vez vinculado, un desplegable con los componentes y sus
// cantidades — para que el operario pueda controlarlos.

import React, { useEffect, useState, useRef } from "react";
import { collection, getDocs, query, where, doc, updateDoc, getDoc, deleteField } from "firebase/firestore";
import { db } from "../../../firebase";
import { esPedidoComponentes, textoPedido } from "../services/parseDescripcionPedido";

const textoDescripcion = textoPedido;

// Fecha de creación del pedido (para filtrar "los de hoy"). Firestore Timestamp,
// con respaldo a finFecha (string YYYY-MM-DD).
function fechaCreacion(p) {
  const t = p?.timestamps?.creado;
  if (t?.toDate) { try { return t.toDate(); } catch { /* noop */ } }
  const secs = t?.seconds ?? t?._seconds;
  if (secs) return new Date(secs * 1000);
  if (p?.finFecha) { const d = new Date(p.finFecha); if (!isNaN(d)) return d; }
  return null;
}
function esDeHoy(p) {
  const d = fechaCreacion(p);
  if (!d) return false;
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}
function qtyDe(it) {
  return it.cant ?? it.cantidad ?? it.qty ?? 0;
}
function nombreDe(it) {
  return it.descripcion || it.desc || it.nombre || it.cod || it.codigo || "—";
}

// Los pedidos "de componentes" de kit viven en 'pedidos_componentes'. Durante la
// transicion pueden estar todavia en 'pedidos' (aun no migrados/ruteados) -> fallback.
async function fetchComponentesPool(deposito, selfId) {
  const [snapC, snapP] = await Promise.all([
    getDocs(collection(db, "pedidos_componentes")),
    getDocs(query(collection(db, "pedidos"), where("deposito", "==", deposito || "R8"))),
  ]);
  const map = new Map();
  snapP.docs.forEach(d => { const p = { id: d.id, ...d.data() }; if (esPedidoComponentes(p)) map.set(d.id, p); });
  snapC.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() })); // la coleccion nueva gana
  if (selfId) map.delete(selfId);
  return [...map.values()];
}
async function fetchComponenteDoc(id) {
  const c = await getDoc(doc(db, "pedidos_componentes", id));
  if (c.exists()) return c.data();
  const pd = await getDoc(doc(db, "pedidos", id)); // fallback transicion
  return pd.exists() ? pd.data() : null;
}

export default function KitVinculo({ pedido, codigoKit, puedeVincular, descripcionKit, descItemKit, onEstado, onVinculado }) {
  // Número que el vendedor escribió en la descripción del kit (pista de qué pedido
  // contiene los componentes) — se muestra para facilitar la vinculación manual.
  const numeroSugerido = String(descripcionKit || "").match(/\b\d{4,6}\b/)?.[0] || String(descItemKit || "").match(/\b\d{4,6}\b/)?.[0] || null;
  // Estado local del vínculo: tras persistir en Firestore no dependemos de que el
  // padre refresque el pedido. undefined = usar el valor del pedido.
  const [linkedLocal, setLinkedLocal] = useState(undefined);
  const vinculoManual = linkedLocal !== undefined ? linkedLocal : (pedido?.kitsVinculados?.[codigoKit] || null);
  const [autoVinculo, setAutoVinculo] = useState(null); // detectado desde la descripción del kit
  const vinculo = vinculoManual || autoVinculo;
  const esAuto = !vinculoManual && !!autoVinculo;
  const [abierto, setAbierto]     = useState(false);
  const [picker, setPicker]       = useState(false);
  const [candidatos, setCandidatos] = useState(null);
  const [compProductos, setCompProductos] = useState(null);
  const [busy, setBusy]           = useState(false);

  // ¿Este kit BLOQUEA la asignación? Sí cuando hay un pedido de componentes
  // disponible (auto-detectado o sugerido por el vendedor) pero AÚN no se vinculó
  // de forma persistente. Se reporta al padre (vista de asignación) vía onEstado.
  const hayCandidato = !!(vinculoManual || autoVinculo || numeroSugerido);
  const bloqueaAsignacion = hayCandidato && !vinculoManual;
  useEffect(() => {
    onEstado?.(codigoKit, bloqueaAsignacion);
    return () => onEstado?.(codigoKit, false);
  }, [codigoKit, bloqueaAsignacion, onEstado]);

  // Auto-detección del pedido de componentes. Tres caminos (el primero que
  // encuentre gana). No pisa un vínculo manual existente. Se hace UN solo escaneo
  // del depósito y el match de números es por la parte numérica (el `numero` viene
  // como "PEDVTA - 23359").
  //   0) La DESCRIPCIÓN DEL ÍTEM del kit (campo DESCITEM de Finnegans) trae el N°
  //      del pedido de componentes (ej. "22394", "REF. 23203"). Lo más directo.
  //   1) Un número de 4-6 dígitos en la descripción/nombre del kit → ese pedido.
  //   2) Un pedido de componentes que menciona el N° de ESTE pedido en su
  //      descripción (ej. "COMPOSICION KIT - 23359").
  useEffect(() => {
    if (vinculoManual) { setAutoVinculo(null); return; }
    let cancel = false;
    const numDe = (v) => String(v || "").match(/\d{4,6}/)?.[0] || null;
    (async () => {
      try {
        const otros = await fetchComponentesPool(pedido.deposito, pedido.id);
        if (cancel) return;
        const porNumero = (n) => n && otros.find(p => numDe(p.numero) === n);

        // ── Camino 0: N° en la descripción del ítem del kit ──
        const compItem = porNumero(numDe(descItemKit));
        if (compItem) { setAutoVinculo({ pedidoId: compItem.id, numero: compItem.numero || compItem.id, via: 0 }); return; }

        // ── Camino 1: N° suelto en la descripción/nombre del kit ──
        for (const n of (String(descripcionKit || "").match(/\d{4,6}/g) || [])) {
          const comp = porNumero(n);
          if (comp) { setAutoVinculo({ pedidoId: comp.id, numero: comp.numero || n, via: 1 }); return; }
        }

        // ── Camino 2: un pedido de componentes referencia el N° de este pedido ──
        const miNumero = numDe(pedido.numero);
        if (miNumero) {
          const comp = otros.find(p => esPedidoComponentes(p) && new RegExp(`\\b${miNumero}\\b`).test(textoDescripcion(p)));
          if (comp) { setAutoVinculo({ pedidoId: comp.id, numero: comp.numero || comp.id, via: 2 }); return; }
        }
        setAutoVinculo(null);
      } catch (e) { console.error("[kit auto]", e); if (!cancel) setAutoVinculo(null); }
    })();
    return () => { cancel = true; };
  }, [vinculoManual, descItemKit, descripcionKit, pedido.deposito, pedido.id, pedido.numero]);

  // ── Auto-vínculo ────────────────────────────────────────────────────────────
  // Se vincula SOLO (sin el clic de "✓ Confirmar") cuando la detección es
  // inequívoca: Camino 0 (el vendedor escribió el N° en la descripción del ítem
  // del kit) o Camino 2 (un pedido de componentes nombra a este pedido). El Camino 1
  // (número suelto en el nombre del kit, más ambiguo) sigue pidiendo confirmación.
  const autoPersistRef = useRef(false);
  useEffect(() => {
    if (autoPersistRef.current) return;
    if (!puedeVincular) return;
    if (vinculoManual) return;
    if (!autoVinculo || (autoVinculo.via !== 0 && autoVinculo.via !== 2)) return;
    autoPersistRef.current = true;
    vincular({ id: autoVinculo.pedidoId, numero: autoVinculo.numero });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoVinculo, vinculoManual, puedeVincular]);

  // Cargar los productos del pedido de componentes vinculado (para el desplegable)
  useEffect(() => {
    if (!vinculo?.pedidoId) { setCompProductos(null); return; }
    let cancel = false;
    (async () => {
      try {
        const data = await fetchComponenteDoc(vinculo.pedidoId);
        if (!cancel) setCompProductos(data ? (data.productos || []) : []);
      } catch { if (!cancel) setCompProductos([]); }
    })();
    return () => { cancel = true; };
  }, [vinculo?.pedidoId]);

  async function abrirPicker() {
    setPicker(true);
    setCandidatos(null);
    try {
      // Componentes salen de 'pedidos_componentes' (+ fallback 'pedidos'). Para saber
      // cuales ya estan vinculados, se escanean los pedidos de kit (en 'pedidos').
      const [componentes, snapPedidos] = await Promise.all([
        fetchComponentesPool(pedido.deposito, pedido.id),
        getDocs(query(collection(db, "pedidos"), where("deposito", "==", pedido.deposito || "R8"))),
      ]);
      const yaVinculados = new Set();
      snapPedidos.docs.forEach(d => {
        const kv = d.data().kitsVinculados;
        if (kv) Object.values(kv).forEach(v => v?.pedidoId && yaVinculados.add(v.pedidoId));
      });

      // El "indicado en la descripción": el pedido de componentes que menciona el
      // N° de ESTE pedido (ej. "COMPOSICION KIT - 23359"). Siempre se ofrece primero.
      const miNumero = String(pedido.numero || "").match(/\d{4,6}/)?.[0] || "";
      const indicado = miNumero
        ? componentes.find(p => new RegExp(`\\b${miNumero}\\b`).test(textoDescripcion(p)))
        : null;
      const indicadoId = indicado?.id || autoVinculo?.pedidoId || null;

      const list = componentes
        .filter(p => {
          if (p.id === indicadoId) return true;      // el indicado, siempre
          if (yaVinculados.has(p.id)) return false;  // ocultar los ya vinculados
          return esDeHoy(p);                          // 2ª opción: solo los de hoy
        })
        .map(p => ({ ...p, _sugerido: p.id === indicadoId }))
        .sort((a, b) => (Number(b._sugerido) - Number(a._sugerido))
          || String(b.numero || "").localeCompare(String(a.numero || "")));
      setCandidatos(list);
    } catch (e) { console.error("[kit] candidatos", e); setCandidatos([]); }
  }

  // Nombre "lindo" del kit (sin el código adelante) para etiquetar los componentes.
  const nombreKit = String(descripcionKit || "").replace(/^\d+\s*/, "").trim() || `Kit ${codigoKit}`;

  async function vincular(cand) {
    setBusy(true);
    try {
      const val = { pedidoId: cand.id, numero: cand.numero || cand.id };
      // Snapshot de los componentes en un campo aparte (kitComponentes). El sync de
      // Finnegans sobrescribe `productos` pero NO toca este campo, así los
      // componentes explotados sobreviven. Se usan para tratarlos como productos
      // normales (categorizar, asignar por parte, preparar, controlar).
      let comps = Array.isArray(cand.productos) ? cand.productos : null;
      if (!comps) {
        try { const data = await fetchComponenteDoc(cand.id); comps = data ? (data.productos || []) : []; }
        catch { comps = []; }
      }
      await updateDoc(doc(db, "pedidos", pedido.id), {
        [`kitsVinculados.${codigoKit}`]: val,
        [`kitComponentes.${codigoKit}`]: {
          nombre: nombreKit, pedidoId: cand.id, numero: val.numero,
          productos: comps, actualizadoAt: new Date().toISOString(),
        },
      });
      setLinkedLocal(val); // marca vinculado localmente → destraba la asignación
      setPicker(false);
      onVinculado?.(); // avisa al padre para recargar y mostrar los componentes expandidos
    } catch (e) { console.error("[kit] vincular", e); alert("No se pudo vincular"); }
    finally { setBusy(false); }
  }

  // Confirma (persiste) el pedido de componentes auto-detectado, con un solo click.
  async function confirmarAuto() {
    if (!autoVinculo?.pedidoId) return;
    await vincular({ id: autoVinculo.pedidoId, numero: autoVinculo.numero });
  }

  // Re-toma los componentes actuales del pedido vinculado (por si cambiaron en
  // Finnegans después de vincular). El snapshot queda fijo hasta que se toca esto.
  async function actualizarComponentes() {
    const target = vinculoManual || autoVinculo;
    if (!target?.pedidoId) return;
    setBusy(true);
    try {
      const data = await fetchComponenteDoc(target.pedidoId);
      const comps = data ? (data.productos || []) : [];
      await updateDoc(doc(db, "pedidos", pedido.id), {
        [`kitComponentes.${codigoKit}`]: {
          nombre: nombreKit, pedidoId: target.pedidoId, numero: target.numero,
          productos: comps, actualizadoAt: new Date().toISOString(),
        },
      });
      onVinculado?.(); // recarga el pedido para reflejar los componentes actualizados
    } catch (e) { console.error("[kit] actualizar", e); alert("No se pudo actualizar"); }
    finally { setBusy(false); }
  }

  async function desvincular() {
    if (!window.confirm("¿Quitar el pedido de componentes vinculado?")) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, "pedidos", pedido.id), {
        [`kitsVinculados.${codigoKit}`]: deleteField(),
        [`kitComponentes.${codigoKit}`]: deleteField(), // saca los componentes expandidos
      });
      setLinkedLocal(null);
      autoPersistRef.current = true; // no volver a auto-vincular tras un desvínculo manual
      onVinculado?.();
    } catch (e) { console.error("[kit] desvincular", e); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: "6px", background: "#faf5ff", border: "1.5px solid #e9d5ff", borderRadius: "10px", padding: "8px 10px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.7rem", fontWeight: 800, color: "#7c3aed", background: "#f3e8ff", padding: "2px 8px", borderRadius: "999px" }}>🧩 KIT</span>

        {vinculo ? (
          <>
            <button onClick={() => setAbierto(a => !a)} style={{ flex: 1, minWidth: 0, textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: "0.8rem", color: "#6b21a8", fontWeight: 700 }}>
              Componentes: pedido #{vinculo.numero} {esAuto && <span style={{ fontSize: "0.62rem", fontWeight: 700, color: "#15803d", background: "#dcfce7", padding: "1px 6px", borderRadius: "999px" }}>auto</span>} {abierto ? "▲" : "▼"}
            </button>
            {puedeVincular && (
              esAuto
                ? <>
                    <button onClick={confirmarAuto} disabled={busy} style={{ flexShrink: 0, fontSize: "0.68rem", fontWeight: 800, color: "#fff", background: "#16a34a", border: "none", borderRadius: "6px", padding: "3px 9px", cursor: "pointer" }}>✓ Confirmar</button>
                    <button onClick={abrirPicker} disabled={busy} style={{ flexShrink: 0, fontSize: "0.68rem", color: "#6b21a8", background: "none", border: "1px solid #e9d5ff", borderRadius: "6px", padding: "3px 7px", cursor: "pointer" }}>Cambiar</button>
                  </>
                : <>
                    <button onClick={actualizarComponentes} disabled={busy} title="Volver a tomar los componentes del pedido vinculado" style={{ flexShrink: 0, fontSize: "0.68rem", fontWeight: 700, color: "#0369a1", background: "none", border: "1px solid #bae6fd", borderRadius: "6px", padding: "3px 9px", cursor: "pointer" }}>↻ Actualizar</button>
                    <button onClick={abrirPicker} disabled={busy} style={{ flexShrink: 0, fontSize: "0.68rem", fontWeight: 700, color: "#6b21a8", background: "none", border: "1px solid #e9d5ff", borderRadius: "6px", padding: "3px 9px", cursor: "pointer" }}>Cambiar</button>
                    <button onClick={desvincular} disabled={busy} style={{ flexShrink: 0, fontSize: "0.68rem", color: "#dc2626", background: "none", border: "1px solid #fecaca", borderRadius: "6px", padding: "3px 7px", cursor: "pointer" }}>Quitar</button>
                  </>
            )}
          </>
        ) : puedeVincular ? (
          <>
            {numeroSugerido && (
              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6b21a8", background: "#f3e8ff", border: "1px solid #e9d5ff", padding: "2px 8px", borderRadius: "999px" }}>
                📄 Vendedor indicó: pedido #{numeroSugerido}
              </span>
            )}
            <button onClick={abrirPicker} disabled={busy} style={{ fontSize: "0.75rem", fontWeight: 700, color: "#fff", background: "#7c3aed", border: "none", borderRadius: "8px", padding: "6px 11px", cursor: "pointer" }}>
              🧩 Vincular componentes
            </button>
          </>
        ) : (
          <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
            {numeroSugerido ? `Componentes en pedido #${numeroSugerido} (sin vincular)` : "Sin componentes vinculados"}
          </span>
        )}
      </div>

      {/* Desplegable de componentes */}
      {vinculo && abierto && (
        <div style={{ marginTop: "8px", borderTop: "1px dashed #e9d5ff", paddingTop: "8px" }}>
          {compProductos === null ? (
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#94a3b8" }}>Cargando componentes…</p>
          ) : compProductos.length === 0 ? (
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#94a3b8" }}>El pedido vinculado no tiene productos.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {compProductos.map((it, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "0.8rem" }}>
                  <span style={{ color: "#334155", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nombreDe(it)}</span>
                  <span style={{ flexShrink: 0, fontWeight: 700, color: "#7c3aed" }}>×{qtyDe(it)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Picker de pedido de componentes */}
      {picker && (
        <div onClick={() => setPicker(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: "560px", maxHeight: "75vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 16px 8px", borderBottom: "1px solid #e2e8f0" }}>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800 }}>Vincular pedido de componentes</h3>
              <p style={{ margin: "3px 0 0", fontSize: "0.78rem", color: "#64748b" }}>El indicado en la descripción + otros pedidos de componentes de hoy sin vincular · {pedido.deposito}</p>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
              {candidatos === null ? (
                <p style={{ color: "#94a3b8", fontSize: "0.85rem" }}>Buscando…</p>
              ) : candidatos.length === 0 ? (
                <p style={{ color: "#94a3b8", fontSize: "0.85rem" }}>No hay pedidos de componentes disponibles (sin vincular y de hoy). Si el correcto es de otro día, pedí al vendedor que lo indique en la descripción del kit.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {candidatos.map(c => (
                    <button key={c.id} onClick={() => vincular(c)} disabled={busy}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: "11px 13px", borderRadius: "10px", border: `1.5px solid ${c._sugerido ? "#86efac" : "#e9d5ff"}`, background: c._sugerido ? "#f0fdf4" : "#faf5ff", cursor: "pointer", textAlign: "left" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: "0.88rem", color: "#0f172a", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                          #{c.numero || c.id}
                          {c._sugerido && <span style={{ fontSize: "0.62rem", fontWeight: 800, color: "#15803d", background: "#dcfce7", padding: "1px 7px", borderRadius: "999px" }}>📄 Indicado en la descripción</span>}
                        </div>
                        <div style={{ fontSize: "0.76rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.cliente || "—"} · {(c.productos || []).length} productos</div>
                      </div>
                      <span style={{ flexShrink: 0, color: "#7c3aed", fontWeight: 700, fontSize: "0.82rem" }}>Vincular →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ padding: "10px 16px 18px" }}>
              <button onClick={() => setPicker(false)} style={{ width: "100%", padding: "12px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", fontWeight: 700, color: "#475569", cursor: "pointer" }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
